import React, { useState, useEffect, useRef } from "react";
import useDebouncedEffect from "../utils/useDebouncedEffect";
import { RenderFieldExtensionCtx } from "datocms-plugin-sdk";
import { FieldValue, Attachment, Asset, CustomField } from "../types";
import get from "lodash/get";

type BrandfolderSearchProps = {
  ctx: RenderFieldExtensionCtx;
};

// The proxy authenticates the caller with the editor's DatoCMS access token,
// present only when the plugin has the `currentUserAccessToken` permission
// granted. Surface a clear error if it's missing rather than sending a request
// the proxy would reject.
const requireUserToken = (ctx: RenderFieldExtensionCtx): string => {
  const token = ctx.currentUserAccessToken;
  if (!token) {
    throw new Error(
      'Missing DatoCMS access token — grant this plugin the "Current user access token" permission.'
    );
  }
  return token;
};

const altTextKey = "Alt Text (125 characters max)";
const creditPhotoKey = "Credit/Photographer";

const extensionVariants: Record<string, string[]> = {
  "jpg": ["jpg", "jpeg", "jpe", "jif", "jfif"],
  "png": ["png"],
  "svg": ["svg", "svgz"],
  "gif": ["gif"],
  "webp": ["webp"],
  "avif": ["avif"],
  "mp4": ["mp4", "m4v"],
  "webm": ["webm"],
};

// List of file extensions to exclude (blacklist)
const excludedExtensions = ["ai", "psd", "eps", "pdf", "zip", "rar", "tif", "tiff"];

const fileTypeOptions = [
  { value: "jpg", label: "jpg", mimetype: "image/jpeg" },
  { value: "png", label: "png", mimetype: "image/png" },
  { value: "svg", label: "svg", mimetype: "image/svg+xml" },
  { value: "gif", label: "gif", mimetype: "image/gif" },
  { value: "webp", label: "webp", mimetype: "image/webp" },
  { value: "avif", label: "avif", mimetype: "image/avif" },
  { value: "mp4", label: "mp4", mimetype: "video/mp4" },
  { value: "webm", label: "webm", mimetype: "video/webm" },
];

const getAssetDetails = async (assetId: string, token: string) => {
  const url = `/api/brandfolder-proxy?resource=asset&id=${encodeURIComponent(assetId)}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("API response error text:", errorText);
      throw new Error(
        `Failed to fetch asset details ${response.status}: ${errorText}`
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Failed to fetch asset details:", error);
    throw error;
  }
};

const getVideoThumbnail = (attachmentId: string, version?: string, width = 200, height = 150): string => {
  if (!attachmentId) return "";
  
  const versionPath = version ? `/v/${version}` : '';
  
  return `https://assets2.brandfolder.io/bf-boulder-prod/${attachmentId}${versionPath}/element.png?height=${height}&width=${width}`;
};

const getSelectedAttachmentUrl = (attributes: any) => {
  if (attributes.is_video || (attributes.mimetype && attributes.mimetype.startsWith('video/'))) {
    if (attributes.thumbnail_url) {
      const matches = attributes.thumbnail_url.match(/bf-boulder-prod\/([a-z0-9]+)\/v\/([0-9]+)\//i);
      if (matches && matches.length >= 3) {
        const attachmentId = matches[1];
        const version = matches[2];
        return getVideoThumbnail(attachmentId, version, 200, 150);
      }
    }
    
    if (attributes.parent_asset && attributes.parent_asset.thumbnail_url) {
      return attributes.parent_asset.thumbnail_url;
    }
  }
  
  if (attributes.cdn_url) {
    const url = attributes.cdn_url;
      return url.includes('?') 
        ? `${url}&width=300` 
        : `${url}?width=300`;
  }
  
  if (attributes.thumbnail_url) {
    return attributes.thumbnail_url;
  }

  const url = attributes.cdn_url || '';
  return url.includes('?') ? `${url}&width=300` : `${url}?width=300`;
};

const transformToCdnUrl = (url: string): string => {
  if (!url || url.includes('cdn.bfldr.com')) {
    return url || "";
  }
  
  try {
    if (url.includes('assets2.brandfolder.io')) {
      const matches = url.match(/assets2\.brandfolder\.io\/(?:[^/]+)\/([a-z0-9-]+)\/(?:v\/[0-9]+\/)?(?:original|original\/)(.+)$/i);
      
      if (matches && matches.length >= 3) {
        const assetId = matches[1];
        let filename = decodeURIComponent(matches[2]);
        
        // Remove all periods except the file extension period
        const lastDotIndex = filename.lastIndexOf('.');
        if (lastDotIndex > 0) {
          const nameWithoutExt = filename.substring(0, lastDotIndex);
          const extension = filename.substring(lastDotIndex);
          filename = nameWithoutExt.replace(/\./g, '') + extension;
        }
        
        filename = filename.replace(/\s+/g, '_');    
        filename = normalizeFilename(filename);
        
        const isImage = /\.(jpg|jpeg|png|webp)$/i.test(filename);
        
        if (isImage) {
          return `https://cdn.bfldr.com/UAHFH8T4/at/${assetId}${filename}?auto=webp&format=webp`;
        }
        
        return `https://cdn.bfldr.com/UAHFH8T4/at/${assetId}${filename}`;
      }
    }

    const genericMatches = url.match(/\/([a-z0-9-]{10,})\/(?:v\/[0-9]+\/)?(?:original|original\/)?([^?&]+\.[a-zA-Z0-9]+)/i);
    if (genericMatches && genericMatches.length >= 3) {
      const assetId = genericMatches[1];
      let filename = decodeURIComponent(genericMatches[2]);
      
      // Remove all periods except the file extension period
      const lastDotIndex = filename.lastIndexOf('.');
      if (lastDotIndex > 0) {
        const nameWithoutExt = filename.substring(0, lastDotIndex);
        const extension = filename.substring(lastDotIndex);
        filename = nameWithoutExt.replace(/\./g, '') + extension;
      }
      
      filename = filename.replace(/\s+/g, '_');
      filename = normalizeFilename(filename);
      
      const isImage = /\.(jpg|jpeg|png|webp)$/i.test(filename);
      
      if (isImage) {
        return `https://cdn.bfldr.com/UAHFH8T4/at/${assetId}${filename}?auto=webp&format=webp`;
      }
      
      return `https://cdn.bfldr.com/UAHFH8T4/at/${assetId}${filename}`;
    }
    
    return url;
  } catch (e) {
    console.error('Error transforming to cdn_url:', e);
    return url;
  }
};

const normalizeFilename = (filename: string): string => {
  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex > 0) {
    const nameWithoutExt = filename.substring(0, lastDotIndex);
    const extension = filename.substring(lastDotIndex);
    let processedFilename = nameWithoutExt.replace(/\./g, '') + extension;
    processedFilename = processedFilename.replace(/\s+/g, '_');
    
    const match = processedFilename.match(/(.+?)(\.[a-zA-Z0-9]+)(\.[a-zA-Z0-9]+)?$/i);
    if (!match) return processedFilename;
    
    let baseName = match[1];
    let ext = match[2].toLowerCase();
    
    if (match[3]) {
      const secondExt = match[3].toLowerCase();
      if (ext.replace('.', '').toLowerCase() === secondExt.replace('.', '').toLowerCase()) {
        return `${baseName}${ext}`;
      } else {
        return `${baseName}${ext}${secondExt}`;
      }
    }
    
    return `${baseName}${ext}`;
  }

  return filename.replace(/\./g, '').replace(/\s+/g, '_');
};

const BrandfolderSearch: React.FC<BrandfolderSearchProps> = ({ ctx }) => {
  const [searchVal, setSearchVal] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [attachmentList, setAttachmentList] = useState<Attachment[]>([]);
  const [showDevMode, setShowDevMode] = useState<boolean>(false);
  const [selectedFileTypes, setSelectedFileTypes] = useState<string[]>([]);
  const [showFiltersDropdown, setShowFiltersDropdown] =
    useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMoreResults, setHasMoreResults] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [searchPerformed, setSearchPerformed] = useState<boolean>(false);
  const [isFullOverwrite, setIsFullOverwrite] = useState<boolean>(false);
  const [fieldValue, setFieldValue] = useState<FieldValue>(
    (get(ctx.formValues, ctx.fieldPath)
      ? JSON.parse(get(ctx.formValues, ctx.fieldPath) as string)
      : { alt_text: "", credit_photo: "" }) as FieldValue
  );

  const resultsPerPage = 20;
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const resultsContainerRef = useRef<HTMLDivElement>(null);

  const getAttachments = async (
    search: string,
    fileTypes: string[],
    page: number = 1,
    perPage: number = 20
  ) => {
    const token = requireUserToken(ctx);
    let searchQuery = search || "";

    const isIdSearch = /^[a-zA-Z0-9-]+$/.test(searchQuery.trim()) && 
                     (searchQuery.includes('-') || searchQuery.length >= 6);
  
    // Only apply filters if it's not an ID search or if specific filters are selected
    if (!isIdSearch || fileTypes.length > 0) {
      // If specific file types are selected, use those (with variants)
      if (fileTypes.length > 0) {
        const queryExtensions = fileTypes.flatMap(type => extensionVariants[type] || [type]);
        const extensionFilters = queryExtensions.map(type => `extension:${type}`).join(" OR ");
        
        if (searchQuery) {
          searchQuery = `${searchQuery} AND (${extensionFilters})`;
        } else {
          searchQuery = extensionFilters;
        }
      } else {
        const allowedExtensions = ["jpg", "jpeg", "jpe", "jif", "jfif", "png", "webp", "avif", "svg", "svgz", "gif", "mp4", "m4v", "webm"];
        const inclusionFilter = `extension:(${allowedExtensions.join(" OR ")})`;
        
        if (searchQuery) {
          searchQuery = `${searchQuery} AND ${inclusionFilter}`;
        } else {
          searchQuery = inclusionFilter;
        }
      }
    }
    
    const queryParams = new URLSearchParams();
    queryParams.append("resource", "search");
    queryParams.append("search", searchQuery);
    queryParams.append("page", page.toString());
    queryParams.append("per", perPage.toString());

    const url = `/api/brandfolder-proxy?${queryParams.toString()}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("API response error text:", errorText);
        throw new Error(
          `Failed to fetch attachments ${response.status}: ${errorText}`
        );
      }
      
      const result = await response.json();
      return result;
    } catch (error) {
      console.error("Failed to fetch assets:", error);
      throw error;
    }
  };

  // Detect clicks outside of the filter dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowFiltersDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Scroll event listener to detect when to load more results
  useEffect(() => {
    const handleScroll = () => {
      if (
        resultsContainerRef.current &&
        hasMoreResults &&
        !loading &&
        attachmentList.length > 0
      ) {
        const { scrollTop, scrollHeight, clientHeight } =
          resultsContainerRef.current;
        if (scrollTop + clientHeight >= scrollHeight - 50) {
          loadMoreAttachments();
        }
      }
    };

    const resultsContainer = resultsContainerRef.current;
    if (resultsContainer) {
      resultsContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (resultsContainer) {
        resultsContainer.removeEventListener("scroll", handleScroll);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMoreResults, loading, attachmentList, currentPage]);

  const handleFileTypeSelection = (fileType: string) => {
    if (selectedFileTypes.includes(fileType)) {
      setSelectedFileTypes(
        selectedFileTypes.filter((type) => type !== fileType)
      );
    } else {
      setSelectedFileTypes([...selectedFileTypes, fileType]);
    }

    setCurrentPage(1);
  };

  const loadMoreAttachments = async () => {
    if (!hasMoreResults || isLoadingMore) return;

    setIsLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const moreAttachments = await getAttachments(
        searchVal,
        selectedFileTypes,
        nextPage,
        resultsPerPage
      );

      if (
        moreAttachments &&
        moreAttachments.data &&
        moreAttachments.data.length > 0
      ) {
        const newAttachments = processAttachments(moreAttachments);
        const newAttachmentsToAdd = newAttachments.slice(0, resultsPerPage);
        const updatedAttachmentList = [
          ...attachmentList,
          ...newAttachmentsToAdd,
        ];
        setAttachmentList(updatedAttachmentList);
        setCurrentPage(nextPage);

        const apiTotalCount = moreAttachments.meta?.total_count || 0;
        setHasMoreResults(
          apiTotalCount > updatedAttachmentList.length ||
            newAttachmentsToAdd.length === resultsPerPage
        );
      } else {
        setHasMoreResults(false);
      }
    } catch (error) {
      console.error("Error loading more results:", error);
      setError(
        `Error loading more results: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    } finally {
      setIsLoadingMore(false);
    }
  };

  const processAttachments = (attachmentResults: any): Attachment[] => {
    const attachments = attachmentResults.included?.filter(
      (item: any) => item.type === "attachments" && item.attributes
    ) || [];

    let processedAttachments = attachments
      .map((attachment: Attachment) => {
        const originalAsset = attachmentResults.data?.find((asset: Asset) =>
          asset.relationships?.attachments?.data?.some(
            (rel: any) => rel.id === attachment.id
          )
        );

        if (originalAsset) {
          const primaryUrl =
            attachment.attributes.cdn_url || attachment.attributes.url || "";
          const transformedUrl = transformToCdnUrl(primaryUrl);
          
          let thumbnailUrl = attachment.attributes.thumbnail_url;
          
          const isVideo = attachment.attributes.mimetype && 
                         attachment.attributes.mimetype.startsWith('video/');
                           
          if (isVideo) {
            if (attachment.attributes.thumbnail_url) {
              const matches = attachment.attributes.thumbnail_url.match(/bf-boulder-prod\/([a-z0-9]+)\/v\/([0-9]+)\//i);
              if (matches && matches.length >= 3) {
                const attachmentId = matches[1];
                const version = matches[2];
                thumbnailUrl = getVideoThumbnail(attachmentId, version);
              } else {
                thumbnailUrl = attachment.attributes.thumbnail_url;
              }
            } else if (attachment.parent_asset && 
                      typeof attachment.parent_asset === 'object' && 
                      'thumbnail_url' in attachment.parent_asset) {
              thumbnailUrl = (attachment.parent_asset as {thumbnail_url: string}).thumbnail_url;
            }
          } else {
            thumbnailUrl = transformedUrl;
            if (thumbnailUrl && attachment.attributes.mimetype?.startsWith('image/')) {
              thumbnailUrl = thumbnailUrl.includes('?') 
                ? `${thumbnailUrl}&width=300` 
                : `${thumbnailUrl}?width=300`;
            }
          }

          return {
            ...attachment,
            attributes: {
              mimetype: attachment.attributes.mimetype,
              extension: attachment.attributes.extension,
              filename: attachment.attributes.filename,
              size: attachment.attributes.size,
              width: attachment.attributes.width,
              height: attachment.attributes.height,
              position: attachment.attributes.position,
              url: transformedUrl,
              thumbnail_url: thumbnailUrl,
              name:
                originalAsset.attributes?.name ||
                attachment.attributes.filename ||
                "",
              description: originalAsset.attributes?.description || "",
            },
            parent_asset: {
              id: originalAsset.id,
              type: originalAsset.type,
            },
          };
        }

        return null;
      })
      .filter((attachment: Attachment): attachment is Attachment => {
        if (!attachment || !attachment.attributes?.url) return false;

        const fileExtension = attachment.attributes.extension?.toLowerCase() || "";
        
        // If no extension or it's in the excluded list, filter it out
        if (!fileExtension || excludedExtensions.includes(fileExtension)) {
          return false;
        }
        
        // If specific filters selected, check against them (including variants)
        if (selectedFileTypes.length > 0) {
          return selectedFileTypes.some(selectedType => 
            (extensionVariants[selectedType] || [selectedType]).includes(fileExtension)
          );
        }

        // Otherwise, include all non-excluded extensions
        return true;
      });

    return processedAttachments;
  };

  const selectAttachment = async (attachment: any) => {
    if (
      typeof attachment === "object" &&
      attachment !== null &&
      Object.keys(attachment as object).length === 0
    ) {
      setIsFullOverwrite(true);
      setFieldValue(null as any);
      ctx.setFieldValue(ctx.fieldPath, null);
      return;
    }

    setIsFullOverwrite(true);

    try {
      const attachmentValue = attachment as Attachment;
      const cdnUrl = transformToCdnUrl(attachmentValue.attributes.url || "");
      
      let thumbnailUrl = "";
      const isVideo = attachmentValue.attributes.mimetype?.startsWith('video/') || 
                     attachmentValue.attributes.is_video;
                       
      if (isVideo) {
        if (attachmentValue.attributes.thumbnail_url) {
          const matches = attachmentValue.attributes.thumbnail_url.match(/bf-boulder-prod\/([a-z0-9]+)\/v\/([0-9]+)\//i);
          if (matches && matches.length >= 3) {
            const attachmentId = matches[1];
            const version = matches[2];
            thumbnailUrl = getVideoThumbnail(attachmentId, version);
          } else {
            thumbnailUrl = attachmentValue.attributes.thumbnail_url;
          }
        } else if (attachmentValue.parent_asset && 
                  typeof attachmentValue.parent_asset === 'object' && 
                  'thumbnail_url' in attachmentValue.parent_asset) {
          thumbnailUrl = (attachmentValue.parent_asset as {thumbnail_url: string}).thumbnail_url;
        }
      } else {
        thumbnailUrl = cdnUrl;
        if (thumbnailUrl && attachmentValue.attributes.mimetype?.startsWith('image/')) {
          thumbnailUrl = thumbnailUrl.includes('?') 
            ? `${thumbnailUrl}&width=300` 
            : `${thumbnailUrl}?width=300`;
        }
      }

      const initialFieldValue: FieldValue = {
        alt_text: "",
        credit_photo: "",
        attributes: {
          name:
            attachmentValue.attributes.name ||
            attachmentValue.attributes.filename ||
            "",
          cdn_url: cdnUrl,
          thumbnail_url: thumbnailUrl,
          filename: attachmentValue.attributes.filename,
          mimetype: attachmentValue.attributes.mimetype,
          extension: attachmentValue.attributes.extension,
          description: attachmentValue.attributes.description || "",
          width: attachmentValue.attributes.width,
          height: attachmentValue.attributes.height,
          is_video:
            attachmentValue.attributes.mimetype?.startsWith("video/") || false,
        },
        parent_asset: attachmentValue.parent_asset,
      };

      setFieldValue(initialFieldValue);
      setAttachmentList([]);
      setSearchVal("");
      setCurrentPage(1);
      setHasMoreResults(false);
      setError("");

      if (attachmentValue.parent_asset && attachmentValue.parent_asset.id) {
        try {
          const assetDetails = await getAssetDetails(
            attachmentValue.parent_asset.id,
            requireUserToken(ctx)
          );

          if (assetDetails.data) {
            const updatedValue: FieldValue = {
              ...initialFieldValue,
              parent_asset: attachmentValue.parent_asset,
            };

            if (assetDetails.included) {
              const customFields = assetDetails.included.filter(
                (item: any) => item.type === "custom_field_values"
              );

              customFields.forEach((field: CustomField) => {
                if (field.attributes?.key === altTextKey) {
                  updatedValue.alt_text = field.attributes.value || "";
                }
                if (field.attributes?.key === creditPhotoKey) {
                  updatedValue.credit_photo = field.attributes.value || "";
                }
              });
            }

            setFieldValue(updatedValue);
          }
        } catch (error) {
          console.error(
            "Could not load parent asset details to get alt text and credit:",
            error
          );
        }
      }
    } catch (error) {
      console.error("Error selecting attachment:", error);
      setError("Could not select the attachment. Please try again.");
    }
  };

  const handleFieldUpdate = (field: string, value: string) => {
    setIsFullOverwrite(false);
    setFieldValue({ ...fieldValue, [field]: value });
  };

  useDebouncedEffect(
    () => {
      if (fieldValue === null) {
        return; 
      }
      
      if (fieldValue && fieldValue.attributes) {
        if (isFullOverwrite) {
          const storedValue = {
            alt_text: fieldValue.alt_text || "",
            credit_photo: fieldValue.credit_photo || "",
            attributes: {
              name: fieldValue.attributes.name,
              cdn_url: fieldValue.attributes.cdn_url,
              thumbnail_url: fieldValue.attributes.thumbnail_url,
              filename: fieldValue.attributes.filename,
              mimetype: fieldValue.attributes.mimetype,
              extension: fieldValue.attributes.extension,
              description: fieldValue.attributes.description,
              width: fieldValue.attributes.width,
              height: fieldValue.attributes.height,
            },
          };

          ctx.setFieldValue(ctx.fieldPath, JSON.stringify(storedValue, null, 2));
          setTimeout(() => setIsFullOverwrite(false), 500);
        } else {
          const currentValue = get(ctx.formValues, ctx.fieldPath);
          let existingData = {};
          
          if (currentValue) {
            try {
              existingData = JSON.parse(currentValue as string);
            } catch (e) {
              console.error("Error parsing existing field data:", e);
            }
          }
          
          const storedValue = {
            ...existingData,
            alt_text: fieldValue.alt_text || "",
            credit_photo: fieldValue.credit_photo || "",
          };

          ctx.setFieldValue(ctx.fieldPath, JSON.stringify(storedValue, null, 2));
        }
      }
    },
    300,
    [fieldValue, isFullOverwrite]
  );

  useDebouncedEffect(
    () => {
      setError("");

      if (!searchVal || searchVal.trim().length === 0) {
        setAttachmentList([]);
        setSearchPerformed(false);
        setCurrentPage(1);
        setHasMoreResults(false);
        return;
      }

      setLoading(true);
      setAttachmentList([]);
      setSearchPerformed(true);
      setCurrentPage(1);
      setError("");

      getAttachments(
        searchVal,
        selectedFileTypes,
        1,
        resultsPerPage
      )
        .then(async (attachmentResults) => {
          if (
            !attachmentResults.data ||
            !Array.isArray(attachmentResults.data) ||
            attachmentResults.data.length === 0
          ) {
            setHasMoreResults(false);
            return;
          }

          const processedAttachments = processAttachments(attachmentResults);

          // Only set the first page of results (maximum resultsPerPage)
          const firstPageResults = processedAttachments.slice(
            0,
            resultsPerPage
          );
          setAttachmentList(firstPageResults);

          // Check if there are more results to load
          const apiTotalCount = attachmentResults.meta?.total_count || 0;
          setHasMoreResults(
            apiTotalCount > firstPageResults.length ||
              processedAttachments.length > firstPageResults.length
          );
        })
        .catch((e) => {
          console.error("Search error:", e);
          setError(
            `Error retrieving search results: ${e.message || "Unknown error"}`
          );
        })
        .finally(() => {
          setLoading(false);
        });
    },
    300,
    [searchVal, selectedFileTypes]
  );

  return (
    <div className="brandfolder-search">
      {fieldValue && fieldValue.attributes ? (
        <>
          <div className="selected">
            {fieldValue.attributes.extension &&
            fieldValue.attributes.extension.toLowerCase() === "svg" ? (
              <div className="svg-background" style={{ display: 'inline-block' }}>
                <img
                  src={getSelectedAttachmentUrl(fieldValue.attributes)}
                  alt={fieldValue.attributes.filename}
                  style={{ display: 'block' }}
                />
              </div>
            ) : (
              <div className="checkered-background" style={{ display: 'inline-block' }}>
                <img
                  src={getSelectedAttachmentUrl(fieldValue.attributes)}
                  alt={fieldValue.attributes.filename}
                  style={{ display: 'block' }}
                />
              </div>
            )}
            <div className="selected-attachment-info">
              <p className="selected-attachment-name" style={{ marginBottom: -4 }}>
                {fieldValue.attributes.name}
              </p>
              <p className="selected-attachment-filename" style={{ color: '#666', fontSize: '0.9em', marginBottom: 8 }}>
                {fieldValue.attributes.filename}
              </p>
              <div className="result-tags">
                {fieldValue.attributes.extension && (
                  <span
                    className={`file-type-tag file-type-${fieldValue.attributes.extension.toLowerCase()}`}
                  >
                    {fieldValue.attributes.extension.toUpperCase()}
                  </span>
                )}
                {(fieldValue.attributes.width || 0) > 0 &&
                  (fieldValue.attributes.height || 0) > 0 && (
                    <span className="dimensions-tag">
                      {fieldValue.attributes.width}x{fieldValue.attributes.height}
                    </span>
                )}
              </div>
            </div>
            <i tabIndex={-1} role="button" onClick={() => selectAttachment({})}>
              X
            </i>
          </div>
          <div className="edit">
            <div>
              <p>{altTextKey}</p>
              <input
                type="text"
                value={fieldValue.alt_text || ""}
                onChange={(e) => handleFieldUpdate("alt_text", e.target.value)}
              />
            </div>
          </div>
          <div className="edit">
            <div>
              <p>Image Credit</p>
              <input
                type="text"
                value={fieldValue.credit_photo || ""}
                onChange={(e) => handleFieldUpdate("credit_photo", e.target.value)}
              />
            </div>
          </div>
          <div className="dev-mode-toggle">
            <label>
              <input
                type="checkbox"
                checked={showDevMode}
                onChange={() => setShowDevMode(!showDevMode)}
              />
              Developer Mode
            </label>
          </div>
          {showDevMode && (
            <div className="dev-mode">
              <pre>{JSON.stringify(fieldValue, null, 2)}</pre>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="search-container">
            <div className="search-with-filters">
              <input
                placeholder="Search for assets..."
                onChange={(e) => setSearchVal(e.target.value)}
                value={searchVal}
              />
              <button
                ref={buttonRef}
                className={`filter-button ${
                  selectedFileTypes.length > 0 ? "has-filters" : ""
                }`}
                onClick={() => setShowFiltersDropdown(!showFiltersDropdown)}
                type="button"
                aria-label="Filter by file type"
              >
                <span className="filter-icon"></span>
                {selectedFileTypes.length > 0 && (
                  <span className="filter-count-badge">
                    {selectedFileTypes.length}
                  </span>
                )}
              </button>

              {showFiltersDropdown && (
                <div ref={dropdownRef} className="filters-dropdown">
                  <div className="filters-header">
                    <div className="filters-title">Advanced Filters</div>
                    <div className="filters-actions">
                      {selectedFileTypes.length > 0 && (
                        <button
                          className="filters-action-btn clear-btn"
                          onClick={() => {
                            setSelectedFileTypes([]);
                            setCurrentPage(1);
                          }}
                        >
                          Clear
                        </button>
                      )}
                      <button
                        className="filters-action-btn hide-btn"
                        onClick={() => setShowFiltersDropdown(false)}
                      >
                        Hide Filters
                      </button>
                    </div>
                  </div>

                  <div className="filter-section">
                    <div className="filter-section-title">File Types</div>
                    <div className="file-type-options">
                      {fileTypeOptions.map((option) => (
                        <div key={option.value} className="file-type-option">
                          <label>
                            <input
                              type="checkbox"
                              value={option.value}
                              checked={selectedFileTypes.includes(option.value)}
                              onChange={() =>
                                handleFileTypeSelection(option.value)
                              }
                            />
                            <span>{option.label}</span>
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {loading && <p className="loading">Searching...</p>}
          {error && <p className="error">{error}</p>}
          {searchPerformed &&
            attachmentList.length === 0 &&
            !loading &&
            !error && (
              <div className="no-results">
                <p>No results found for "{searchVal}"</p>
                {
                  <p className="filter-hint">
                    Try removing some filters or modifying your search terms
                  </p>
                }
              </div>
            )}

          {attachmentList.length > 0 && (
            <>
              <div className="results" ref={resultsContainerRef}>
                {attachmentList.map((attachment: Attachment, index: number) => {
                  const fileExtension = attachment.attributes.extension || 
                    (attachment.attributes.mimetype ? attachment.attributes.mimetype.split('/')[1] : "");
                  
                  return (
                    <div
                      key={`${attachment.id}-${index}`}
                      tabIndex={-1}
                      role="button"
                      onClick={() => selectAttachment(attachment)}
                      className={`result-item file-type-item-${fileExtension.toLowerCase()}`}
                    >
                      <div className={`result-image-container ${fileExtension.toLowerCase() === "svg" ? "svg-container" : ""}`}>
                        <img
                          alt={
                            attachment.attributes.name ||
                            attachment.attributes.filename
                          }
                          src={
                            attachment.attributes.cdn_url ||
                            attachment.attributes.thumbnail_url
                          }
                        />
                      </div>
                      <div className="result-info">
                        <p className="result-name" style={{ textAlign: 'center', display: 'block', marginBottom: -10 }}>
                          {attachment.attributes.name}
                        </p>
                        <p className="result-filename" style={{ textAlign: 'center', display: 'block', color: '#666', fontSize: 12, marginBottom: 4 }}>
                          {attachment.attributes.filename}
                        </p>
                        <div className="result-tags">
                          <span
                            className={`file-type-tag file-type-${fileExtension.toLowerCase()}`}
                          >
                            {fileExtension.toUpperCase()}
                          </span>
                          {(attachment.attributes.width || 0) > 0 &&
                            (attachment.attributes.height || 0) > 0 && (
                              <span className="dimensions-tag">
                                {attachment.attributes.width}x
                                {attachment.attributes.height}
                              </span>
                            )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {isLoadingMore && (
                  <div className="loading-more">Loading more results...</div>
                )}
              </div>
            </>
          )}

          <div className="dev-mode-toggle">
            <label>
              <input
                type="checkbox"
                checked={showDevMode}
                onChange={() => setShowDevMode(!showDevMode)}
              />
              Developer Mode
            </label>
          </div>
        </>
      )}
    </div>
  );
};

export default BrandfolderSearch;
